import { createMachine } from 'xstate';

import getBootstrapGuards from 'common/bento/lib/bootstrap/getBootstrapGuards';
import shared from 'common/bento/shared';
import { AnyMachineEvent } from 'common/bento/types/Abstract';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';

import actions, { Action } from './actions';
import guards, { Guard } from './guards';
import model, { Context } from './model';
import services, { Service } from './services';

export enum State {
  INIT = 'init',

  START_SIGNATURE = 'start-signature',
  SIGNATURE = 'signature',
  FOURTHLINE_NATIVE_WORKFLOW = 'fourthline-native-workflow',

  FINISH_SIGNATURE = 'finish-signature',

  SUCCESS = 'success',
  INELIGIBLE = 'ineligible',

  ABORT = 'abort',
  DONE = 'done',
}

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'qes',

    initial: State.INIT,

    on: {
      '*': {
        actions: shared.actions.unhandled(),
      },
      // Silence the event if fired outside of the states handling it
      RESTART_SIGNATURE: {},
    },

    states: {
      [State.INIT]: {
        always: [
          ...getBootstrapGuards([State.FINISH_SIGNATURE]),
          {
            cond: Guard.IsIneligibleToQes,
            target: State.INELIGIBLE,
          },
          {
            target: State.START_SIGNATURE,
          },
        ],
      },

      [State.START_SIGNATURE]: {
        invoke: {
          onDone: [
            {
              actions: Action.AssignSignatureContext,
              cond: Guard.ShouldDoNativeFourthlineWorkflow,
              target: State.FOURTHLINE_NATIVE_WORKFLOW,
            },
            {
              actions: Action.AssignSignatureContext,
              target: State.SIGNATURE,
            },
          ],
          onError: [
            {
              actions: [
                shared.actions.serverTrack({
                  event: 'Applicant QES Held',
                }),
              ],
              cond: Guard.IsInvalidIdentityProviderError,
              target: State.ABORT,
            },
            {
              actions: [
                shared.actions.serverTrack({
                  event: 'Applicant QES Ineligible',
                }),
              ],
              cond: Guard.IsIneligibleIdentityError,
              target: State.INELIGIBLE,
            },
            {
              cond: Guard.IsSilentError,
              target: State.ABORT,
            },
            {
              actions: shared.actions.error(),
              target: State.ABORT,
            },
          ],
          src: Service.StartSignature,
        },
      },

      [State.FOURTHLINE_NATIVE_WORKFLOW]: {
        // This state shows the native ID check screen, we want to track when it's shown
        entry: shared.actions.trackView(),

        invoke: {
          onDone: [
            {
              actions: shared.actions.error(),
              cond: Guard.HasNativeFourthlineWorkflowFailed,
              /**
               * @todo Add a new state for the error
               */
              target: State.DONE,
            },
            { target: State.DONE },
          ],

          onError: {
            actions: shared.actions.error(),
            target: State.DONE,
          },

          src: Service.CompleteNativeFourthlineWorkflow,
        },

        on: {
          PREV: State.DONE,
        },
      },

      [State.SIGNATURE]: {
        entry: shared.actions.trackView(),

        on: {
          INVALID_IDENTITY_PROVIDER_ERROR: State.ABORT,
          NEXT: State.FINISH_SIGNATURE,
          PREV: State.ABORT,
          RESTART_SIGNATURE: State.START_SIGNATURE,
        },
      },

      [State.FINISH_SIGNATURE]: {
        invoke: {
          onDone: {
            actions: shared.actions.track({
              name: 'Onboarding QES Completed',
            }),
            target: State.SUCCESS,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.START_SIGNATURE,
          },
          src: Service.FinishSignature,
        },
      },

      [State.SUCCESS]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: State.DONE,
          PREV: State.DONE,
        },
      },

      [State.INELIGIBLE]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: State.DONE,
          PREV: State.ABORT,
        },
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },
    },
  },
  {
    actions,
    guards,
    services,
  },
);

export default machine;
